import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Divider,
  Chip,
} from '@mui/material';
import {
  Payment as EncaissementIcon,
  Receipt as FactureIcon,
  Assignment as ProjetIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { GlassBackdrop, GlassSurface } from '../../components/GlassDashboard';
import { encaissementsAPI, financierDashboardAPI, FinancierDashboardStats } from '../../services/api';
import { Encaissement, MotifEncaissement } from '../../types/encaissement';

const FinancierOverview: React.FC = () => {
  const [encaissementsList, setEncaissementsList] = useState<Encaissement[]>([]);
  const [stats, setStats] = useState<FinancierDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, recentData] = await Promise.all([
          financierDashboardAPI.getStats(),
          encaissementsAPI.getAll(),
        ]);
        setStats(statsData);
        setEncaissementsList((recentData || []).slice(0, 5));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Encaissement stats (server-aggregated)
  const totalEncaissements = stats?.encaissements.total ?? 0;
  const paiementFactureCount = stats?.encaissements.paiement_facture ?? 0;
  const avanceProjetCount = stats?.encaissements.avance_devis ?? 0;
  const totalMontant = stats?.encaissements.montant_total ?? 0;

  const statsCards = [
    {
      title: 'Total Encaissements',
      value: totalEncaissements,
      icon: <EncaissementIcon sx={{ fontSize: 40 }} />,
      color: '#9C27B0',
    },
    {
      title: 'Paiements Factures',
      value: paiementFactureCount,
      icon: <FactureIcon sx={{ fontSize: 40 }} />,
      color: '#2196F3',
    },
    {
      title: 'Avances Devis',
      value: avanceProjetCount,
      icon: <ProjetIcon sx={{ fontSize: 40 }} />,
      color: '#FF9800',
    },
    {
      title: 'Montant Total',
      value: `${totalMontant.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH`,
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      color: '#4CAF50',
      isAmount: true,
    },
  ];

  return (
    <GlassBackdrop>
      <Typography variant="h4" gutterBottom sx={{ color: '#9C27B0', fontWeight: 600 }}>
        Tableau de Bord Financier
      </Typography>

      {/* Stats Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statsCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <GlassSurface accentColor={card.color} sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {card.title}
                  </Typography>
                  <Typography variant={card.isAmount ? 'h5' : 'h4'} sx={{ fontWeight: 600 }}>
                    {loading ? '...' : card.value}
                  </Typography>
                </Box>
                <Box sx={{ color: card.color }}>{card.icon}</Box>
              </Box>
            </GlassSurface>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <GlassSurface accentColor="#9C27B0" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#9C27B0' }}>
              Encaissements Récents
            </Typography>
            {loading ? (
              <Typography>Chargement...</Typography>
            ) : encaissementsList.length === 0 ? (
              <Typography color="textSecondary">Aucun encaissement enregistré</Typography>
            ) : (
              <Box>
                {encaissementsList.slice(0, 5).map((enc) => (
                  <Box
                    key={enc.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1.5,
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {enc.reference}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {enc.client_name || '-'}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="body2" fontWeight={600} color="#9C27B0">
                        {Number(enc.montant_encaisse).toLocaleString('fr-FR', {
                          minimumFractionDigits: 2,
                        })}{' '}
                        DH
                      </Typography>
                      <Chip
                        label={
                          enc.motif_encaissement === MotifEncaissement.PAIEMENT_FACTURE
                            ? 'Facture'
                            : 'Devis'
                        }
                        size="small"
                        color={
                          enc.motif_encaissement === MotifEncaissement.PAIEMENT_FACTURE
                            ? 'primary'
                            : 'secondary'
                        }
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </GlassSurface>
        </Grid>

        <Grid item xs={12} md={6}>
          <GlassSurface accentColor="#9C27B0" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#9C27B0' }}>
              Modules Disponibles
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="body1" sx={{ flex: 1 }}>
                  Décaissements
                </Typography>
                <Chip label="Actif" size="small" color="success" />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="body1" sx={{ flex: 1 }}>
                  Rapprochement Bancaire
                </Typography>
                <Chip label="Actif" size="small" color="success" />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5 }}>
                <Typography variant="body1" sx={{ flex: 1 }}>
                  Trésorerie
                </Typography>
                <Chip label="Actif" size="small" color="success" />
              </Box>
            </Box>
          </GlassSurface>
        </Grid>
      </Grid>
    </GlassBackdrop>
  );
};

export default FinancierOverview;
